import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import {
  BORDER_RADIUS,
  ALERT_ICON_TYPE_SUCCESS,
  META,
  GOOGLE,
  TIKTOK,
  TIKTOK_BUSINESS,
  LINKEDIN,
} from "../../../utils/constants";
import { logoPlatform } from "../../../utils/string";
import React, { useState } from "react";
import {
  BUTTON_RECONNECT,
  BUTTON_DISCONNECT,
  BUTTON_DISCONNECT_ACCOUNT,
  ALERT_DESCRIPTION_DISCONNECT_FACEBOOK,
  ALERT_DESCRIPTION_DISCONNECT_GOOGLE,
  ALERT_DESCRIPTION_DISCONNECT_TIKTOK_BUSINESS,
  DISCONNECTED_ACCOUNT,
  ARE_YOU_SURE_TO_DISCONNECT_ACCOUNT,
  ALERT_DESCRIPTION_DISCONNECT_LINKEDIN,
} from "../../../i18n/keysTranslations";
import useButtonActionPlatform from "../../../hooks/useButtonActionPlatform";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import Button from "../../../components/Buttons/Button";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import { ConfirmAlert, SimpleAlert } from "../../../components/Alerts/Alerts";
import {
  startRevokeFacebookAccount,
  startRevokeGoogleAdsAccount,
  startRevokeLinkedInAdsAccount,
  startRevokeTiktokBusinessAccount,
} from "../../../actions/user";

import { LaunchError } from "../../../utils/errors";

const VerifyIntegrationScopesItem = ({ data }) => {
  const { platform, integrationID } = data;
  const BUTTON_ACTIONS_COMPONENTS = useButtonActionPlatform();
  const normalizedPlatform = platform === TIKTOK_BUSINESS ? TIKTOK : platform;
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const [isRemoving, setIsRemoving] = useState(false);

  const getDisconnectAlertText = () => {
    if (platform === META) return t(ALERT_DESCRIPTION_DISCONNECT_FACEBOOK);
    if (platform === GOOGLE) return t(ALERT_DESCRIPTION_DISCONNECT_GOOGLE);
    if (platform === TIKTOK_BUSINESS || platform === TIKTOK)
      return t(ALERT_DESCRIPTION_DISCONNECT_TIKTOK_BUSINESS);
    if (platform === LINKEDIN) return t(ALERT_DESCRIPTION_DISCONNECT_LINKEDIN);
    return "";
  };

  const onRemoveAccount = async () => {
    const result = await ConfirmAlert({
      title: t(ARE_YOU_SURE_TO_DISCONNECT_ACCOUNT),
      text: getDisconnectAlertText(),
      confirmButtonText: t(BUTTON_DISCONNECT_ACCOUNT),
    });

    if (!result.isConfirmed) return;

    setIsRemoving(true);
    let response = false;

    try {
      if (platform === META) {
        response = await dispatch(
          startRevokeFacebookAccount({
            id: integrationID,
            onChangeLoading: () => {},
          })
        );
      } else if (platform === GOOGLE) {
        response = await dispatch(
          startRevokeGoogleAdsAccount({
            id: integrationID,
          })
        );
      } else if (platform === TIKTOK_BUSINESS || platform === TIKTOK) {
        response = await dispatch(
          startRevokeTiktokBusinessAccount({
            id: integrationID,
          })
        );
      } else if (platform === LINKEDIN) {
        response = await dispatch(
          startRevokeLinkedInAdsAccount({
            id: integrationID,
          })
        );
      }

      if (response) {
        SimpleAlert({
          title: t(DISCONNECTED_ACCOUNT),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onRemoveAccount",
          capture: "exception",
        })
      );
      console.error("Error removing account:", error);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "row",
        },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "flex-start", md: "center" },
        gap: 1,
        border: `${INPUT_COLOR_BORDER} 1px solid`,
        p: 2,
        borderRadius: BORDER_RADIUS,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "flex-start", md: "center" },
          gap: 1,
        }}
      >
        <Avatar
          sx={{
            ".MuiAvatar-img": {
              objectFit: "contain",
            },
            width: 25,
            height: 25,
          }}
          src={logoPlatform(platform)}
          variant="square"
        />
        <Typography
          variant="body1"
          sx={{
            flexGrow: 1,
          }}
        >
          {data.name}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          width: {
            xs: "100%",
            sm: "100%",
            md: "auto",
          },
          flexDirection: { xs: "column", sm: "column", md: "row" },
        }}
      >
        <Button
          color="secondary"
          onClick={onRemoveAccount}
          loading={isRemoving}
          disabled={isRemoving}
          sx={{
            minWidth: { xs: "100%", sm: "100%", md: 130 },
            width: { xs: "100%", sm: "100%", md: "auto" },
            flex: { xs: "1 1 auto", sm: "1 1 auto", md: "0 0 auto" },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {t(BUTTON_DISCONNECT)}
        </Button>
        {BUTTON_ACTIONS_COMPONENTS[normalizedPlatform] &&
          React.cloneElement(BUTTON_ACTIONS_COMPONENTS[normalizedPlatform], {
            mode: "add",
            text: BUTTON_RECONNECT,
            sx: {
              minWidth: { xs: "100%", sm: "100%", md: 130 },
              width: { xs: "100%", sm: "100%", md: "auto" },
              flex: { xs: "1 1 auto", sm: "1 1 auto", md: "0 0 auto" },
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          })}
      </Box>
    </Box>
  );
};

export default VerifyIntegrationScopesItem;
